import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Joins class names, and lets a caller's class win.
 *
 * Plain string concatenation looked sufficient until the primitives arrived.
 * A component that sets `w-full` and a caller that passes `w-44` both end up
 * in the attribute, and which one applies comes down to the order Tailwind
 * happened to emit them in — so the override works on one screen and silently
 * does nothing on another. `twMerge` resolves conflicting utilities in favour
 * of the last one, which is the caller.
 *
 * ── Why this is configured rather than imported plain ──
 *
 * `twMerge` groups utilities by what they set, and it knows Tailwind's OWN
 * names. The design system replaced the type scale with named sizes —
 * `text-body`, `text-h2`, `text-caption` — and to a stock `twMerge` those are
 * unrecognised `text-*` classes, which it files under text COLOUR.
 *
 * The consequence was shipped and invisible:
 *
 *     twMerge('bg-primary-600 text-white', 'h-10 px-4 text-body')
 *       → 'bg-primary-600 h-10 px-4 text-body'      // text-white is gone
 *
 * `Button` composes variant then size, and every size string carries a
 * `text-*` from this scale — so `text-white` was dropped from EVERY primary,
 * success and danger button in the product. They rendered dark grey on
 * indigo, green and red: legible enough that nobody filed it, and nowhere
 * near the contrast those colours were chosen for.
 *
 * Telling the merger about the scale fixes it everywhere at once, and stops
 * the next component that sets a colour and a size from meeting the same
 * thing. `text-sm` never had the problem, which is exactly why it went
 * unnoticed for as long as it did.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{
        text: [
          'display', 'h1', 'h2', 'h3', 'h4',
          'body', 'body-lg', 'small', 'caption', 'micro',
        ],
      }],
    },
  },
});

export function cn(...parts: Array<string | false | null | undefined>): string {
  return twMerge(parts.filter(Boolean).join(' '));
}

/**
 * A 40px place to press on a phone, for a link that is a word in a line.
 *
 * A breadcrumb, a telephone number, "Open in Maps": making the link itself
 * 40px tall would push every line under it down. This grows only what a
 * thumb can hit — an invisible box 8px above and below the text — so the
 * page lays out exactly as it did. Not for links stacked a line apart: their
 * boxes would overlap, and the lower one would win. The utility itself is in
 * the Tailwind preset; this is its name for `cn(...)`.
 */
export const TAP_AREA = 'tap-area';

/**
 * A form's action row, held at the foot of the screen on a phone.
 *
 * On a 3,000px form the Save button is ten thumb-lengths below the field
 * being typed in. Sticky rather than fixed: at the end of the form it sits
 * where it always did, and it never covers the last field. The negative
 * margin runs it to the screen's edges across the page's 16px gutter.
 */
export const STICKY_ACTIONS = 'max-md:sticky max-md:bottom-0 max-md:z-10 max-md:-mx-4 max-md:border-t '
  + 'max-md:border-neutral-200 max-md:bg-white max-md:px-4 max-md:py-3';

/**
 * A hand-written `<table>` as stacked rows on a phone.
 *
 * `DataTable` does this itself. The few tables written by hand — an editable
 * catalogue, a rate card — scrolled sideways inside their box instead, with
 * the column a person had come to edit off the edge. Put each class on its
 * element and a `data-label` on every cell after the first: the header row
 * is not drawn on a phone, so the label is what says which value is which.
 * From `md` none of this applies.
 */
export const STACK = {
  table: 'max-md:block',
  head: 'max-md:hidden',
  body: 'max-md:block',
  row: 'max-md:block max-md:px-3 max-md:py-2.5',
  first: 'max-md:block max-md:px-0 max-md:pb-1',
  cell: 'max-md:flex max-md:min-h-9 max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1 '
    + 'max-md:text-left max-md:before:shrink-0 max-md:before:text-caption max-md:before:font-normal '
    + 'max-md:before:text-neutral-500 max-md:before:content-[attr(data-label)]',
} as const;
